import { type PropsWithChildren, type ReactNode } from 'react'
import { type DialogState } from 'ariakit'
import {
  csx,
  Modal,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  type ModalSize,
  ModalTitle,
  type StyleProp,
} from '@vtex/admin-ui'

import * as styles from './CustomModal.styles'

export type CustomModalProps = {
  state: DialogState
  title: ReactNode
  customAction?: ReactNode
  size?: ModalSize
  subTitle?: ReactNode
  footer?: ReactNode
  withBorder?: boolean
  withMaxHeight?: boolean
  contentClass?: StyleProp
}

/**
 * A custom modal component.
 *
 * @example
 * import { useModalState } from '@vtex/admin-ui'
 * // Usage example
 * const state = useModalState()
 *
 * <CustomModal state={state}>
 *   // Add your modal content here
 * </CustomModal>
 */
export const CustomModal = ({
  state,
  title,
  size = 'large',
  withBorder = true,
  withMaxHeight = true,
  children,
  customAction,
  subTitle,
  footer,
  contentClass = {},
}: PropsWithChildren<CustomModalProps>) => (
  <Modal
    state={state}
    backdropProps={{
      onClick: (evt) => evt.stopPropagation(),
      className: csx(styles.modalBackdrop),
    }}
    className={`${csx(styles.modal)} ${
      withMaxHeight ? styles.modalMaxHeight : ''
    }`}
    size={size}
  >
    {title && (
      <ModalHeader
        className={`${csx(styles.modalHeader)} ${
          withBorder ? styles.modalHeaderBorder : ''
        }`}
      >
        <ModalTitle className={csx(styles.modalTitle)}>
          {title}
          {subTitle && <div>{subTitle}</div>}
        </ModalTitle>
        {customAction}
        <ModalDismiss onClick={state.hide} className={csx(styles.dismiss)} />
      </ModalHeader>
    )}
    <section className={csx({ ...styles.modalContent, ...contentClass })}>
      {children}
    </section>
    {footer && (
      <ModalFooter className={styles.modalFooter}>{footer}</ModalFooter>
    )}
  </Modal>
)
